import React, { useCallback, useEffect, useRef, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@mui/material/styles";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import {
  uaeStates,
  type PortSecurityFormData,
} from "@features/port-security/schema/portSecurityFormSchema";
import { usePortSecurityFormStore } from "@features/port-security/store/usePortSecurityForm.store";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { checkPassportOrEmiratesId } from "@services/securityClearanceApi";
import { useMutation } from "@tanstack/react-query";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { t } from "i18next";
import { FormProvider, useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { formatOnChangeEmiratesIDNumber } from "../utils";

interface IdCheckResponse {
  status?: string;
  expirationDate?: string;
  isPassportOrEmiratesIdNumberDuplicate: boolean;
}
const LANG_BASE = "security-clearance";
const service = "bff-gatepass";
const UAE_NATIONALITY = "United Arab Emirates";
const EMIRATES_ID_GROUP_FIELDS: Array<keyof PortSecurityFormData> = [
  "emiratesIdPlaceOfIssue",
  "emiratesIdNumber",
  "emiratesIdDateOfIssue",
];

const hasEmiratesIdFieldValue = (value: unknown) => {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  return String(value ?? "").trim().length > 0;
};

const EMIRATES_ID_FIELD_VALIDATION_CONTEXT = {
  module: "security_clearance",
  screen: "Security Clearance Emirates ID Details",
  action: "validate_emirates_id_number",
  fromField: "emiratesIdNumber",
  toField: "emiratesIdDuplicateStatus",
  entityType: "security_clearance",
};

const EmiratesIdData: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const previousEmiratesIdFieldsRef = useRef<{
    presentNationality?: string;
    emiratesIdPlaceOfIssue?: string;
    emiratesIdNumber?: string;
    emiratesIdDateOfIssue?: Date | string | null;
  } | null>(null);
  const stateOptions = uaeStates.map((state) => ({
    label: state.state,
    value: state.state,
  }));

  const { isPassportNumberSet, securityClearanceRenewID } = useAuthStore();
  const store = usePortSecurityFormStore();
  const hasEmiratesIdValue = !!store.emiratesIdNumber;
  const { notify } = useNotification();
  const [status, setStatus] = useState<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const theme = useTheme();
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const presentNationality = useWatch({
    control: formData.control,
    name: "presentNationality",
  });
  const emiratesIdPlaceOfIssue = useWatch({
    control: formData.control,
    name: "emiratesIdPlaceOfIssue",
  });
  const emiratesIdNumber = useWatch({
    control: formData.control,
    name: "emiratesIdNumber",
  });
  const emiratesIdDateOfIssue = useWatch({
    control: formData.control,
    name: "emiratesIdDateOfIssue",
  });

  const checkEmiratesId = useMutation({
    mutationFn: ({
      service,
      emiratesId,
      id,
      trackFieldNavigation,
    }: {
      service: string;
      emiratesId?: string;
      id?: string;
      trackFieldNavigation?: boolean;
    }) => {
      const execute = () =>
        checkPassportOrEmiratesId({
          service,
          emiratesId,
          id,
        }) as Promise<IdCheckResponse>;

      if (trackFieldNavigation) {
        return runFieldNavigation(EMIRATES_ID_FIELD_VALIDATION_CONTEXT, execute);
      }

      return execute();
    },
    onSuccess: async (data: IdCheckResponse) => {
      const { isPassportOrEmiratesIdNumberDuplicate, status, expirationDate } = data;
      if (isPassportOrEmiratesIdNumberDuplicate && status) {
        setStatus(status);
        if (expirationDate) {
          setExpirationDate(expirationDate);
        }
      } else {
        setStatus(null);
      }
    },
    onError: (error: Error) => {
      setStatus(null);
      notify({
        title: t(`${LANG_BASE}.notifications.check-id-error-title`),
        description: error?.message ?? t(`${LANG_BASE}.notifications.check-id-error-description`),
        type: "error",
      });
      console.error("Error checking ID:", error);
    },
  });

  const handleEmiratesIdChange = useCallback(
    (value: string) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        if (value && value.length >= 18 && securityClearanceRenewID === null) {
          checkEmiratesId.reset();
          checkEmiratesId.mutate({ service, emiratesId: value, trackFieldNavigation: true });
        } else {
          setStatus(null);
        }
      }, 500);

      setDebounceTimer(timer);
    },
    [debounceTimer, checkEmiratesId],
  );

  useEffect(() => {
    const storeEmiratesIdNumber = store.emiratesIdNumber;
    const formEmiratesIdNumber = formData.getValues("emiratesIdNumber");
    if (storeEmiratesIdNumber && storeEmiratesIdNumber !== formEmiratesIdNumber) {
      formData.setValue("emiratesIdNumber", storeEmiratesIdNumber, { shouldDirty: true });
    }

    if (
      formEmiratesIdNumber &&
      formEmiratesIdNumber.length === 18 &&
      securityClearanceRenewID === null
    ) {
      checkEmiratesId.reset();
      checkEmiratesId.mutate({ service, emiratesId: formEmiratesIdNumber });
    } else {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  useEffect(() => {
    const previous = previousEmiratesIdFieldsRef.current;
    previousEmiratesIdFieldsRef.current = {
      presentNationality,
      emiratesIdPlaceOfIssue,
      emiratesIdNumber,
      emiratesIdDateOfIssue,
    };

    if (!previous) {
      return;
    }

    const isUaeNationality = presentNationality?.trim() === UAE_NATIONALITY;
    const didClearEmiratesIdField =
      (hasEmiratesIdFieldValue(previous.emiratesIdPlaceOfIssue) &&
        !hasEmiratesIdFieldValue(emiratesIdPlaceOfIssue)) ||
      (hasEmiratesIdFieldValue(previous.emiratesIdNumber) &&
        !hasEmiratesIdFieldValue(emiratesIdNumber)) ||
      (hasEmiratesIdFieldValue(previous.emiratesIdDateOfIssue) &&
        !hasEmiratesIdFieldValue(emiratesIdDateOfIssue));

    if (isUaeNationality || !didClearEmiratesIdField) {
      return;
    }

    void formData.trigger?.(EMIRATES_ID_GROUP_FIELDS);
  }, [
    emiratesIdDateOfIssue,
    emiratesIdNumber,
    emiratesIdPlaceOfIssue,
    formData,
    presentNationality,
  ]);

  const renderMessage = () => {
    switch (status) {
      case "APPROVED":
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.success.main }}
          >
            <CheckCircleOutlineIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-approved`, { date: expirationDate })}</p>
          </div>
        );
      case "PERMANENTLY_REJECTED":
      case "SUSPENDED":
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.error.main }}
          >
            <HighlightOffIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-rejected`)}</p>
          </div>
        );
      case null:
        return null;
      default:
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.warning.main }}
          >
            <WarningAmberIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-pending`)}</p>
          </div>
        );
    }
  };

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.particular-of-emirates-id")}>
        <div className="flex flex-col">
          <FormItem<PortSecurityFormData, "emiratesIdNumber">
            name="emiratesIdNumber"
            type="text"
            label={t("security-clearance.emirates-id-number")}
            inputLabel={t("security-clearance.emirates-id-label")}
            testId="security-emiratesIdNumber"
            disabled={
              hasEmiratesIdValue && !isPassportNumberSet && securityClearanceRenewID === null
            }
            onFieldChange={(key: string, value: unknown) => {
              updateFormData(key, value);
              handleEmiratesIdChange(value as string);
            }}
            formatOnChange={formatOnChangeEmiratesIDNumber}
          />
          {status && (
            <div id="messages-wrapper" className="mb-6 w-full">
              {renderMessage()}
            </div>
          )}
        </div>
        <FormItem<PortSecurityFormData, "emiratesIdPlaceOfIssue">
          name="emiratesIdPlaceOfIssue"
          type="autocomplete"
          options={stateOptions}
          label={t("security-clearance.place-of-issue")}
          testId="security-emiratesIdPlaceOfIssue"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "emiratesIdExpiryDate">
          name="emiratesIdExpiryDate"
          type="date-picker"
          label={t("security-clearance.expiry-date")}
          testId="security-emiratesIdExpiryDate"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-past"
        />
        <FormItem<PortSecurityFormData, "emiratesIdDateOfIssue">
          name="emiratesIdDateOfIssue"
          type="date-picker"
          label={t("security-clearance.date-of-issue")}
          testId="security-emiratesIdDateOfIssue"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-future"
        />
      </FormSection>
    </FormProvider>
  );
};

export default EmiratesIdData;
